/**
 * Fix Workspace Owner
 * 
 * This script updates the dev workspace owner to the correct user
 * and sets their member role to "owner"
 */

import { 
  doc, 
  getDoc, 
  setDoc,
  collection, 
  getDocs,
  query,
  where
} from "firebase/firestore";
import { db } from "../firebase";

const DEV_WORKSPACE_ID = "90Yq3rWMUo50j8kXOzr7";
const TARGET_EMAIL = "zargarianvahagn@gmail.com";

/**
 * Fix workspace owner to the correct user
 */
export async function fixWorkspaceOwner(): Promise<void> {
  console.log("🔧 Fixing workspace owner...");
  console.log(`   Workspace ID: ${DEV_WORKSPACE_ID}`);
  console.log(`   Target Email: ${TARGET_EMAIL}`);

  try {
    // Step 1: Find the user ID by searching members collection
    console.log("\n📋 Step 1: Finding user ID in workspace members...");
    const membersRef = collection(db, "workspaces", DEV_WORKSPACE_ID, "members");
    const membersSnapshot = await getDocs(membersRef);
    
    let targetUserId: string | null = null;
    let targetMemberDoc: any = null;

    membersSnapshot.docs.forEach((memberDoc) => {
      const memberData = memberDoc.data();
      const memberEmail = (memberData.email || "").toLowerCase().trim();
      
      if (memberEmail === TARGET_EMAIL.toLowerCase().trim()) {
        targetUserId = memberDoc.id;
        targetMemberDoc = memberDoc;
        console.log(`   ✅ Found user: ${targetUserId}`);
        console.log(`      Email: ${memberData.email}`);
        console.log(`      Name: ${memberData.name || memberData.displayName || "Unknown"}`);
        console.log(`      Current Role: ${memberData.role || "user"}`);
      }
    });

    if (!targetUserId || !targetMemberDoc) {
      console.error("\n   ❌ User not found in workspace members!");
      console.log("   Please make sure you're a member of the workspace first.");
      return;
    }

    // Step 2: Update workspace ownerId
    console.log("\n🔄 Step 2: Updating workspace ownerId...");
    const workspaceRef = doc(db, "workspaces", DEV_WORKSPACE_ID);
    const workspaceSnap = await getDoc(workspaceRef);
    
    if (!workspaceSnap.exists()) {
      console.error("   ❌ Workspace not found!");
      return;
    }

    const workspaceData = workspaceSnap.data();
    const currentOwnerId = workspaceData?.ownerId;
    console.log(`   Current owner: ${currentOwnerId || "unknown"}`);
    console.log(`   New owner: ${targetUserId}`);

    if (currentOwnerId === targetUserId) {
      console.log("   ✅ Workspace owner is already correct (skipping)");
    } else {
      try {
        await setDoc(workspaceRef, {
          ownerId: targetUserId,
        }, { merge: true });
        console.log("   ✅ Workspace ownerId updated successfully");
      } catch (error: any) {
        console.error("   ❌ Failed to update workspace ownerId:", error.message);
        console.log("\n📝 MANUAL STEP REQUIRED:");
        console.log("   To set yourself as owner, do this in Firebase Console:");
        console.log("   1. Go to: https://console.firebase.google.com");
        console.log("   2. Select project: budget-trace");
        console.log("   3. Go to Firestore Database");
        console.log(`   4. Navigate to: workspaces → ${DEV_WORKSPACE_ID}`);
        console.log("   5. Edit document");
        console.log(`   6. Change 'ownerId' from '${currentOwnerId}' to '${targetUserId}'`);
        console.log("   7. Save");
        // Continue to try updating member role even if workspace ownerId update failed
      }
    }

    // Step 3: Update member role to "owner" (try this even if workspace ownerId update failed)
    console.log("\n🔄 Step 3: Updating member role to 'owner'...");
    const memberRef = doc(db, "workspaces", DEV_WORKSPACE_ID, "members", targetUserId);
    const memberSnap = await getDoc(memberRef);
    
    if (!memberSnap.exists()) {
      console.error("   ❌ Member document not found!");
      return;
    }

    const memberData = memberSnap.data();
    const currentRole = memberData?.role || "user";
    console.log(`   Current role: ${currentRole}`);
    console.log(`   New role: owner`);

    if (currentRole === "owner") {
      console.log("   ✅ Member role is already 'owner' (skipping)");
    } else {
      try {
        await setDoc(memberRef, {
          role: "owner",
        }, { merge: true });
        console.log("   ✅ Member role updated to 'owner' successfully");
        console.log("   Note: You may need to update workspace ownerId manually in Firebase Console");
      } catch (error: any) {
        console.error("   ❌ Failed to update member role:", error.message);
        console.log("\n📝 MANUAL STEPS REQUIRED:");
        console.log("   You need to update both the workspace ownerId AND your member role:");
        console.log("\n   1. Workspace OwnerId:");
        console.log("      - Go to: https://console.firebase.google.com");
        console.log("      - Select project: budget-trace");
        console.log("      - Go to Firestore Database");
        console.log(`      - Navigate to: workspaces → ${DEV_WORKSPACE_ID}`);
        console.log("      - Edit document");
        console.log(`      - Change 'ownerId' from 'bypass-user' to '${targetUserId}'`);
        console.log("      - Save");
        console.log("\n   2. Member Role:");
        console.log(`      - Navigate to: workspaces → ${DEV_WORKSPACE_ID} → members → ${targetUserId}`);
        console.log("      - Edit document");
        console.log(`      - Change 'role' from '${currentRole}' to 'owner'`);
        console.log("      - Save");
      }
    }

    console.log("\n✅ Workspace owner fix completed!");
    console.log("   Please refresh the page to see the changes.");
  } catch (error: any) {
    console.error("\n❌ Error fixing workspace owner:", error);
    console.error("   Error details:", error.message);
  }
}

/**
 * Make it available in browser console for easy access
 */
if (typeof window !== "undefined") {
  (window as any).fixWorkspaceOwner = fixWorkspaceOwner;
  console.log("🔧 Fix workspace owner script loaded! Run: window.fixWorkspaceOwner()");
}
